import { useEffect, useRef } from "react";

import type { BrokerAccountRef } from "@/utils/brokerAccountRef";
import { brokerAccountNotesEntityId } from "@/utils/brokerAccountNotesEntityId";
import { createNote } from "../../services/notesApi";
import type { Note } from "../../types/notes";
import { useNotesList } from "./useNotesList";

const LEGACY_STORAGE_KEY = "tessera.executing_account.notes.v1";
const MIGRATED_STORAGE_KEY = "tessera.executing_account.notes.migrated.v1";

type NoteStore = Record<string, Note[]>;
type MigratedStore = Record<string, boolean>;

function readLegacyStore(): NoteStore {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as NoteStore) : {};
  } catch {
    return {};
  }
}

function writeLegacyStore(store: NoteStore): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(store));
}

function readMigratedStore(): MigratedStore {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(MIGRATED_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as MigratedStore) : {};
  } catch {
    return {};
  }
}

function markEntityMigrated(entityId: string): void {
  if (typeof window === "undefined") return;
  const store = readMigratedStore();
  store[entityId] = true;
  window.localStorage.setItem(MIGRATED_STORAGE_KEY, JSON.stringify(store));
}

function clearLegacyNotesForEntity(entityId: string): void {
  const store = readLegacyStore();
  if (!(entityId in store)) return;
  delete store[entityId];
  writeLegacyStore(store);
}

/** API-backed notes for executing broker accounts, with one-time localStorage migration. */
export function useExecutingAccountNotesList(account: BrokerAccountRef) {
  const entityId = brokerAccountNotesEntityId(account);
  const notesList = useNotesList({ entityType: "broker_account", entityId });
  const migratingRef = useRef(false);

  useEffect(() => {
    if (notesList.loading || notesList.error || migratingRef.current) return;
    if (readMigratedStore()[entityId]) return;

    const legacyNotes = readLegacyStore()[entityId];
    if (!Array.isArray(legacyNotes) || legacyNotes.length === 0) {
      markEntityMigrated(entityId);
      return;
    }

    migratingRef.current = true;
    void (async () => {
      try {
        for (const note of legacyNotes) {
          const body = String(note.body || "").trim();
          if (!body) continue;
          await createNote({
            entity_type: "broker_account",
            entity_id: entityId,
            body,
          });
        }
        clearLegacyNotesForEntity(entityId);
        markEntityMigrated(entityId);
        await notesList.reload();
      } catch {
        // Keep legacy notes until the next successful load + migration attempt.
      } finally {
        migratingRef.current = false;
      }
    })();
  }, [entityId, notesList.loading, notesList.error, notesList.reload]);

  return notesList;
}
